import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmKitConfigFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { WsmAmount } from "@dashboard/wsmShared/money";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { sectionMessages, statusMessages } from "../../messages";
import { wsmKitAddPath, wsmKitUrl } from "../../urls";
import { getKitDiscountAmountProps } from "../../utils";
import { columnMessages, messages } from "./messages";

const numberOfColumns = 4;

/**
 * One shape for both discount kinds: WsmAmount decides, and the kind is named
 * underneath so "10%" and "$40.00" in one column stay tellable apart.
 */
const KitDiscountCell = ({ kit }: { kit: WsmKitConfigFragment }) => {
  const intl = useIntl();
  const props = getKitDiscountAmountProps(kit.discountKind, kit.discountAmount, kit.currencyCode);

  return (
    <>
      <WsmAmount {...props} />
      <Text size={2} color="default2" display="block">
        {intl.formatMessage(
          props.basis === "percent"
            ? sectionMessages.discountPercent
            : sectionMessages.discountFixed,
        )}
      </Text>
    </>
  );
};

export interface WsmKitListPageProps {
  kits: WsmKitConfigFragment[] | undefined;
  query: string;
  rowNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onRowNumberChange: (rowNumber: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmKitListPage = ({
  kits,
  query,
  rowNumber,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onRowNumberChange,
  onNextPage,
  onPreviousPage,
}: WsmKitListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.sectionTitle)}>
        <Button onClick={() => navigate(wsmKitAddPath)} data-test-id="wsm-kit-list-create">
          {intl.formatMessage(messages.createKit)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="wsm-kit-list">
        <DashboardCard.Content paddingX={0}>
          <Box paddingX={6} paddingTop={4} paddingBottom={2}>
            <SearchInput
              initialSearch={query}
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              onSearchChange={onQueryChange}
            />
          </Box>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(columnMessages.collection)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.discount)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.freightClass)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.status)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                kits,
                kit => (
                  <TableRowLink
                    key={kit ? kit.id : "skeleton"}
                    hover={!!kit}
                    href={kit ? wsmKitUrl(kit.id) : undefined}
                    data-test-id="wsm-kit-row"
                  >
                    <TableCell>{kit?.collection?.name ?? <Skeleton />}</TableCell>
                    <TableCell data-test-id="wsm-kit-row-discount">
                      {kit ? <KitDiscountCell kit={kit} /> : <Skeleton />}
                    </TableCell>
                    <TableCell>{kit ? kit.freightClass || "-" : <Skeleton />}</TableCell>
                    <TableCell>
                      {kit ? (
                        <Pill
                          data-test-id="wsm-kit-row-status"
                          color={kit.active ? "success" : "error"}
                          label={intl.formatMessage(
                            kit.active ? statusMessages.live : statusMessages.draft,
                          )}
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-kit-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Text size={3} color="default2">
                        {intl.formatMessage(messages.noKits)}
                      </Text>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={{ rowNumber }}
                  onUpdateListSettings={(_, value) => onRowNumberChange(value as number)}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmKitListPage;
